import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
const formatRawDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr.includes('T') ? dateStr : dateStr.replace(' ', 'T'));
  if (isNaN(d.getTime())) return dateStr;
  const day = d.getDate();
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const month = months[d.getMonth()];
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12 || 12;
  return `${day} ${month}, ${hours}:${minutes} ${ampm}`;
};
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Wallet, ArrowUpRight, ArrowDownLeft, Building, History, TrendingUp, Loader2, RefreshCw, CreditCard } from 'lucide-react';
import { API_ENDPOINTS } from '../config/api';

const parseJsonResponse = async (response) => {
  const text = await response.text();
  if (!text || !text.trim()) return {};
  try {
    return JSON.parse(text);
  } catch (_) {
    throw new Error('Invalid response from server. Please try again.');
  }
};

const WalletPage = () => {
  // Dynamic data states
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [error, setError] = useState('');
  const [cancellingId, setCancellingId] = useState(null);
  const [toast, setToast] = useState('');
  
  const navigate = useNavigate();
  const balanceFetchRef = useRef(false);

  // Fetch wallet balance
  const fetchBalance = async () => {
    const token = localStorage.getItem('reddyannabook_token');
    let user = null;
    try {
      const u = localStorage.getItem('reddyannabook_user');
      if (u) user = JSON.parse(u);
    } catch (_) {}
    if (!token || (user && user.id === 'demo') || balanceFetchRef.current) return;
    
    try {
      balanceFetchRef.current = true;
      setBalanceLoading(true);
      setError('');
      
      const response = await fetch(API_ENDPOINTS.WALLET_BALANCE, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await parseJsonResponse(response);

      if (response.ok) {
        setBalance(data.data?.balance ?? data.data);
        const userData = JSON.parse(localStorage.getItem('reddyannabook_user') || '{}');
        const bal = data.data?.balance ?? data.data;
        if (bal) {
          userData.balance = bal.main_balance;
          localStorage.setItem('reddyannabook_user', JSON.stringify(userData));
        }
      } else {
        throw new Error(data.message || 'Failed to fetch balance');
      }
    } catch (err) {
      setError(err.message);
      console.error('Balance fetch error:', err);
    } finally {
      setBalanceLoading(false);
      balanceFetchRef.current = false;
    }
  };

  // Fetch transactions
  const fetchTransactions = async () => {
    const token = localStorage.getItem('reddyannabook_token');
    let user = null;
    try {
      const u = localStorage.getItem('reddyannabook_user');
      if (u) user = JSON.parse(u);
    } catch (_) {}
    if (!token || (user && user.id === 'demo')) {
      setTransactions([]);
      return;
    }
    try {
      setTransactionsLoading(true);
      setError('');
      
      const response = await fetch(API_ENDPOINTS.WALLET_TRANSACTIONS, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await parseJsonResponse(response);

      if (response.ok) {
        setTransactions(data.data || []);
      } else {
        throw new Error(data.message || 'Failed to fetch transactions');
      }
    } catch (err) {
      setError(err.message);
      console.error('Transactions fetch error:', err);
    } finally {
      setTransactionsLoading(false);
    }
  };

  // Fetch withdrawal accounts
  const fetchAccounts = async () => {
    const token = localStorage.getItem('reddyannabook_token');
    let user = null;
    try {
      const u = localStorage.getItem('reddyannabook_user');
      if (u) user = JSON.parse(u);
    } catch (_) {}
    if (!token || (user && user.id === 'demo')) {
      setAccounts([]);
      return;
    }
    try {
      const response = await fetch(API_ENDPOINTS.WALLET_ACCOUNTS, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await parseJsonResponse(response);

      if (response.ok) {
        setAccounts(data.data?.accounts || []);
      } else {
        console.warn('Failed to fetch accounts:', data.message);
      }
    } catch (err) {
      console.error('Accounts fetch error:', err);
    }
  };

  // Cancel a pending withdrawal
  const handleCancelWithdraw = async (transactionId) => {
    if (!window.confirm('Cancel this withdrawal?\nYour coins will be returned to your account.')) return;
    setCancellingId(transactionId);
    try {
      const token = localStorage.getItem('reddyannabook_token');
      const res = await fetch(API_ENDPOINTS.WALLET_CANCEL_WITHDRAW, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ transaction_id: transactionId })
      });
      const data = await parseJsonResponse(res);
      if (res.ok && data.success !== false) {
        setToast(data.data?.message || 'Withdrawal cancelled! Coins returned.');
        fetchTransactions();
        fetchBalance();
        setTimeout(() => setToast(''), 4000);
      } else {
        setToast(data.message || 'Failed to cancel withdrawal');
        setTimeout(() => setToast(''), 4000);
      }
    } catch (err) {
      setToast('Network error. Please try again.');
      setTimeout(() => setToast(''), 4000);
    } finally {
      setCancellingId(null);
    }
  };

  // Navigate to deposit page
  const handleDeposit = () => {
    navigate('/deposit');
  };

  // Navigate to withdraw page
  const handleWithdraw = () => {
    navigate('/withdraw');
  };

  // Initial data fetch (skip API calls for demo or no-token so page doesn't crash)
  useEffect(() => {
    const token = localStorage.getItem('reddyannabook_token');
    let user = null;
    try {
      const u = localStorage.getItem('reddyannabook_user');
      if (u) user = JSON.parse(u);
    } catch (_) {}

    if (!token || (user && user.id === 'demo')) {
      setBalance({
        main_balance: user?.balance ?? 0,
        bonus_balance: user?.bonus_balance ?? user?.bonusWallet ?? 0,
        net_exposure: user?.net_exposure ?? user?.netExposure ?? 0,
        total_balance: (user?.balance ?? 0) + (user?.bonus_balance ?? user?.bonusWallet ?? 0),
      });
      setTransactions([]);
      setAccounts([]);
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        await Promise.all([fetchBalance(), fetchTransactions(), fetchAccounts()]);
      } catch (err) {
        console.error('Initial data fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Balance updates via 'balance-update' event only (no polling)

  const getTransactionIcon = (type) => {
    switch(type) {
      case 'deposit':
        return <ArrowDownLeft className="w-4 h-4 text-green-600" />;
      case 'withdrawal':
        return <ArrowUpRight className="w-4 h-4 text-red-600" />;
      case 'bet_win':
        return <TrendingUp className="w-4 h-4 text-green-600" />;
      case 'bet_loss':
        return <TrendingUp className="w-4 h-4 text-red-600" />;
      default:
        return <History className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusBadge = (status, tx = {}) => {
    const s = status?.toLowerCase();
    if (s === 'completed' || s === 'approved') return <Badge className="bg-green-600 text-white text-[10px]">Approved</Badge>;
    if (s === 'pending') return <Badge className="bg-yellow-500 text-white text-[10px]">Pending</Badge>;
    if (s === 'processing') return <Badge className="bg-blue-500 text-white text-[10px]">Processing</Badge>;
    if (s === 'failed' || s === 'rejected') return <Badge className="bg-red-600 text-white text-[10px]">Rejected</Badge>;
    if (s === 'cancelled' && tx.master_note) return <Badge className="bg-red-600 text-white text-[10px]">Rejected</Badge>;
    if (s === 'cancelled') return <Badge className="bg-gray-500 text-white text-[10px]">Cancelled</Badge>;
    return <Badge className="bg-gray-400 text-white text-[10px]">{status || 'Unknown'}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center gap-2">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>Loading wallet...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white px-6 py-3 rounded-xl shadow-lg text-sm font-medium animate-pulse">
          {toast}
        </div>
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Wallet className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">My Wallet</h1>
                <p className="text-gray-600">Manage your deposits, withdrawals and balance</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {balanceLoading && (
                <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={fetchBalance}
                disabled={balanceLoading}
                className="flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </Button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
              <span className="text-red-700 text-sm">{error}</span>
            </div>
          </div>
        )}
        
        {/* Action Buttons */}
        <div className="flex gap-3 mb-6">
          <Button 
            onClick={handleDeposit}
            className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
          >
            <ArrowDownLeft className="w-4 h-4" />
            Deposit
          </Button>

          <Button 
            onClick={handleWithdraw}
            variant="outline" 
            className="flex items-center gap-2"
          >
            <ArrowUpRight className="w-4 h-4" />
            Withdraw
          </Button>
        </div>

        {/* Balance Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card className="bg-gradient-to-r from-[#C10930] to-[#6D081D] text-white border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm font-medium">Main Balance</p>
                  <p className="text-2xl font-bold">
                    {balanceLoading ? (
                      <Loader2 className="w-6 h-6 animate-spin" />
                    ) : (
                      `₹${balance ? Number(balance.main_balance || 0).toLocaleString('en-IN') : '0'}`
                    )}
                  </p>
                </div>
                <Wallet className="w-8 h-8 text-green-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-yellow-100 text-sm font-medium">Bonus Balance</p>
                  <p className="text-2xl font-bold">
                    {balanceLoading ? (
                      <Loader2 className="w-6 h-6 animate-spin" />
                    ) : (
                      `₹${balance ? Number(balance.bonus_balance).toLocaleString() : '0'}`
                    )}
                  </p>
                </div>
                <TrendingUp className="w-8 h-8 text-yellow-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm font-medium">Net Exposure</p>
                  <p className="text-2xl font-bold">
                    {balanceLoading ? (
                      <Loader2 className="w-6 h-6 animate-spin" />
                    ) : (
                      `₹${balance ? Number(balance.net_exposure).toLocaleString() : '0'}`
                    )}
                  </p>
                </div>
                <CreditCard className="w-8 h-8 text-purple-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm font-medium">Total Balance</p>
                  <p className="text-2xl font-bold">
                    {balanceLoading ? (
                      <Loader2 className="w-6 h-6 animate-spin" />
                    ) : (
                      `₹${balance ? Number(balance.total_balance).toLocaleString() : '0'}`
                    )}
                  </p>
                </div>
                <History className="w-8 h-8 text-blue-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <Tabs defaultValue="transactions" className="w-full">
            <TabsList className="grid w-full grid-cols-3 bg-gray-50 rounded-t-lg">
              <TabsTrigger value="transactions" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
                Transactions
              </TabsTrigger>
              <TabsTrigger value="summary" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
                Summary
              </TabsTrigger>
              <TabsTrigger value="settings" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
                Settings
              </TabsTrigger>
            </TabsList>

            <TabsContent value="transactions" className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Transaction History</h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchTransactions}
                  disabled={transactionsLoading}
                  className="flex items-center gap-2"
                >
                  {transactionsLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  Refresh
                </Button>
              </div>
              
              {transactionsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
                  <span className="ml-2 text-gray-500">Loading transactions...</span>
                </div>
              ) : transactions.length === 0 ? (
                <div className="text-center py-8">
                  <History className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 mb-3">No deposit or withdrawal history yet</p>
                  <Button onClick={() => navigate('/deposit')} className="bg-green-600 hover:bg-green-700">Make a Deposit</Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {transactions.map((tx) => {
                    const isCredit = tx.type === 'deposit' || tx.type === 'transfer' || tx.type === 'coins_added_by_master';
                    const isRecovery = tx.type === 'coin_recovery';
                    const isMasterAction = tx.type === 'transfer' || tx.type === 'coins_added_by_master' || isRecovery;
                    const txLabel = isMasterAction ? (isRecovery ? 'Coins Removed' : 'Coins Added') : (isCredit ? 'Deposit' : 'Withdrawal');
                    const methodLabel = isMasterAction ? '👤 Master' : (() => { const m = (tx.method || '').toLowerCase(); if (m.includes('upi')) return '📱 UPI'; if (m.includes('bank') || m.includes('transfer')) return '🏦 Bank Transfer'; if (m.includes('card')) return '💳 Card'; return tx.method || '—'; })();
                    const dateStr = formatRawDate(tx.created_at || tx.date);
                    const isPositive = isCredit && !isRecovery;
                    return (
                      <Card key={`${tx.type}_${tx.id}`} className="border border-gray-200 hover:shadow-sm transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start">
                            <div className="flex items-start gap-3">
                              <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${isPositive ? 'bg-green-100' : 'bg-red-100'}`}>
                                {isPositive ? <ArrowDownLeft className="w-4 h-4 text-green-600" /> : <ArrowUpRight className="w-4 h-4 text-red-600" />}
                              </div>
                              <div>
                                <h3 className="font-semibold text-gray-900 text-sm">{txLabel} <span className="font-normal text-gray-500">via {methodLabel}</span></h3>
                                <p className="text-xs text-gray-400 mt-0.5">{dateStr}</p>
                                {tx.utr_number && <p className="text-[10px] text-blue-500 font-medium mt-0.5">UTR: {tx.utr_number}</p>}
                                {tx.master_note && (tx.status === 'failed' || tx.status === 'cancelled') && (
                                  <p className="text-[10px] text-red-500 font-medium mt-0.5">Reason: {tx.master_note}</p>
                                )}
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <div className={`text-base font-bold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                                {isPositive ? '+' : '-'}₹{Math.abs(tx.amount || 0).toLocaleString('en-IN')}
                              </div>
                              <div className="mt-1 flex items-center justify-end gap-2">
                                {getStatusBadge(tx.status, tx)}
                                {tx.type === 'withdrawal' && tx.status === 'pending' && (
                                  <button
                                    onClick={() => handleCancelWithdraw(tx.id)}
                                    disabled={cancellingId === tx.id}
                                    className="text-[10px] px-2 py-0.5 bg-red-50 text-red-600 rounded-full hover:bg-red-100 transition-all font-semibold border border-red-200 disabled:opacity-50"
                                  >
                                    {cancellingId === tx.id ? '...' : 'Cancel'}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            <TabsContent value="summary" className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Wallet Summary</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="border border-gray-200">
                  <CardHeader>
                    <CardTitle className="text-gray-900">Account Overview</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Main Balance</span>
                        <span className="font-semibold text-green-600">
                          ₹{balance ? Number(balance.main_balance || 0).toLocaleString('en-IN') : '0'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Bonus Balance</span>
                        <span className="font-semibold text-yellow-600">
                          ₹{balance ? Number(balance.bonus_balance).toLocaleString() : '0'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Net Exposure</span>
                        <span className="font-semibold text-purple-600">
                          ₹{balance ? Number(balance.net_exposure).toLocaleString() : '0'}
                        </span>
                      </div>
                      <hr />
                      <div className="flex justify-between font-bold">
                        <span>Total Balance</span>
                        <span className="text-blue-600">
                          ₹{balance ? Number(balance.total_balance).toLocaleString() : '0'}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border border-gray-200">
                  <CardHeader>
                    <CardTitle className="text-gray-900">Transaction Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Transactions</span>
                        <span className="font-semibold">{transactions.length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Credits</span>
                        <span className="font-semibold text-green-600">
                          {transactions.filter(t => t.type === 'deposit' || t.type === 'transfer' || t.type === 'coins_added_by_master').length}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Debits</span>
                        <span className="font-semibold text-red-600">
                          {transactions.filter(t => t.type === 'withdrawal' || t.type === 'coin_recovery').length}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Credited</span>
                        <span className="font-semibold text-green-600">
                          ₹{transactions.filter(t => (t.type === 'deposit' || t.type === 'transfer' || t.type === 'coins_added_by_master') && t.status === 'completed').reduce((s,t) => s + Math.abs(parseFloat(t.amount)||0), 0).toLocaleString('en-IN')}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Debited</span>
                        <span className="font-semibold text-red-600">
                          ₹{transactions.filter(t => (t.type === 'withdrawal' || t.type === 'coin_recovery') && t.status === 'completed').reduce((s,t) => s + Math.abs(parseFloat(t.amount)||0), 0).toLocaleString('en-IN')}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="settings" className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Wallet Settings</h3>
              <div className="grid gap-6">
                <Card className="border border-gray-200">
                  <CardHeader>
                    <CardTitle className="text-gray-900">Wallet Limits</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="text-sm text-gray-600 font-medium">Daily Deposit Limit</div>
                          <div className="text-xl font-bold text-gray-900">₹50,000</div>
                        </div>
                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="text-sm text-gray-600 font-medium">Daily Withdrawal Limit</div>
                          <div className="text-xl font-bold text-gray-900">₹25,000</div>
                        </div>
                      </div>
                      <Button variant="outline" className="w-full">
                        Request Limit Increase
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border border-gray-200">
                  <CardHeader>
                    <CardTitle className="text-gray-900">Withdrawal Accounts</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {accounts.length === 0 ? (
                        <div className="text-center py-6">
                          <Building className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-500 mb-4">No withdrawal accounts added</p>
                          <Button variant="outline">
                            Add Withdrawal Account
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {accounts.map((account) => (
                            <div key={account.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                              <div className="flex justify-between items-center">
                                <div>
                                  <div className="font-semibold text-gray-900">{account.bank_name}</div>
                                  <div className="text-sm text-gray-600">{account.account_number}</div>
                                  <div className="text-sm text-gray-500">{account.ifsc_code}</div>
                                </div>
                                <Badge className={account.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                                  {account.is_active ? 'Active' : 'Inactive'}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default WalletPage;